import { useState, useEffect, useMemo } from 'react';
import { useEquipmentContext } from '../../contexts/EquipmentContext';
import { PMReport } from '../../types/pmTypes';
import { calculateLaborCost } from '../../utils/pmUtils';
import { parsePmLaborLines } from '../../utils/pmCostTracking';
import type { ScheduledCompletionItem } from '../../utils/pmIncompleteItems';
import { fetchPMReportsByCompany } from '../../graphql/pmQueries';
import { useUserContext } from '../../contexts/UserContext';
import generatePDF, { Options } from 'react-to-pdf';
import { PDF_OPTIONS } from '../../utils/constants';
import { BackButton, Btn } from '../ui';
import { PMIncompleteItemsSection } from './PMIncompleteItemsSection';

interface PMReportDetailProps {
    reportId: string;
    onBack: () => void;
}

/**
 * Individual PM Report detail view.
 * Displays full report: all PM_Service_Item responses, vendor associations,
 * labor entries (broken down by schedule, ST hours, OT hours, total cost),
 * attached photos, digital signature.
 * Labor cost summary using calculateLaborCost.
 *
 * Validates: Requirements 10.4, 6.7
 */
export const PMReportDetail = ({ reportId, onBack }: PMReportDetailProps) => {
    const { companyUnits } = useEquipmentContext();
    const { companyUser, companyOwner } = useUserContext();
    const [report, setReport] = useState<PMReport | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const companyId = (companyUser?.companyId as string) || companyOwner?.companyId || '';

    const getTargetElement = () => document.getElementById('pm-report-detail-content');

    useEffect(() => {
        if (!companyId) return;
        setIsLoading(true);
        fetchPMReportsByCompany(companyId)
            .then((items) => {
                const found = items.find((r) => r.id === reportId);
                if (found) {
                    setReport({
                        id: found.id,
                        companyId: found.companyId,
                        unitId: found.unitId,
                        pmConfigurationId: found.pmConfigurationId,
                        serviceLevelId: found.serviceLevelId,
                        serviceLevelName: found.serviceLevelName,
                        actualUM: found.actualUM,
                        serviceDate: found.serviceDate,
                        technicianId: found.technicianId,
                        technicianName: found.technicianName,
                        laborRateScheduleId: found.laborRateScheduleId,
                        laborRateScheduleName: found.laborRateScheduleName,
                        laborHoursST: found.laborHoursST,
                        laborHoursOT: found.laborHoursOT,
                        laborRateST: found.laborRateST,
                        laborRateOT: found.laborRateOT,
                        totalLaborCost: found.totalLaborCost,
                        reportData:
                            typeof found.reportData === 'string'
                                ? found.reportData
                                : found.reportData != null
                                  ? JSON.stringify(found.reportData)
                                  : null,
                        issueDescription: found.issueDescription,
                        issueTicketCreated: found.issueTicketCreated,
                        issueTicketId: found.issueTicketId,
                        signature: found.signature,
                        photos: found.photos?.filter((p: unknown): p is string => typeof p === 'string') ?? null,
                        status: found.status ?? undefined,
                        createdAt: found.createdAt ?? undefined,
                        updatedAt: found.updatedAt ?? undefined,
                    });
                }
            })
            .catch(err => console.error('Failed to fetch report:', err))
            .finally(() => setIsLoading(false));
    }, [companyId, reportId]);

    // Parse report data (service item responses)
    const reportItems = useMemo<ScheduledCompletionItem[]>(() => {
        if (!report?.reportData) return [];
        try {
            const parsed = JSON.parse(report.reportData);
            return Array.isArray(parsed) ? parsed : parsed.items || [];
        } catch {
            return [];
        }
    }, [report]);

    const laborLines = useMemo(() => parsePmLaborLines(report?.reportData), [report]);
    const geotag = useMemo(() => {
        if (!report?.reportData) return null;
        try {
            const parsed = JSON.parse(report.reportData);
            const point = parsed?.geotag;
            if (typeof point?.latitude === 'number' && typeof point?.longitude === 'number') {
                return point as { latitude: number; longitude: number };
            }
        } catch {
            return null;
        }
        return null;
    }, [report]);

    // Equipment info
    const unit = useMemo(() => {
        if (!report) return null;
        return companyUnits.find((u) => u.id === report.unitId) || null;
    }, [report, companyUnits]);

    const unitDisplayName = useMemo(() => {
        const u = unit as { unitNumber?: string | null; name?: string | null } | null;
        return u?.unitNumber || u?.name || report?.unitId || '';
    }, [unit, report?.unitId]);

    // Computed labor cost
    const computedLaborCost = useMemo(() => {
        if (!report) return 0;
        return calculateLaborCost({
            laborHoursST: report.laborHoursST ?? 0,
            laborHoursOT: report.laborHoursOT ?? 0,
            standardRate: report.laborRateST ?? 0,
            overtimeRate: report.laborRateOT ?? 0,
        });
    }, [report]);

    const formatDate = (dateStr?: string | null) => {
        if (!dateStr) return '—';
        try { return new Date(dateStr).toLocaleDateString(); } catch { return dateStr; }
    };

    const formatCurrency = (val?: number | null) => {
        if (val == null) return '—';
        return `$${val.toFixed(2)}`;
    };

    const handlePrint = () => {
        window.print();
    };

    const handlePDFExport = () => {
        const unitName = unitDisplayName || report?.unitId || 'PM-Report';
        generatePDF(getTargetElement, {
            filename: `PM-Report-${unitName}`,
            ...PDF_OPTIONS,
        } as Options);
    };

    if (isLoading) {
        return <div className="pm-table-empty">Loading report...</div>;
    }

    if (!report) {
        return (
            <div className="pm-service-queue-panel" style={{ padding: '24px' }}>
                <BackButton
                    onClick={onBack}
                    buttonText="Back to Reports"
                    buttonClasses="back-button--inline"
                />
                <div className="pm-table-empty">Report not found.</div>
            </div>
        );
    }

    return (
        <div>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <BackButton
                    onClick={onBack}
                    buttonText="Back to Reports"
                    buttonClasses="back-button--inline"
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Btn
                        variant="secondary-outlined"
                        buttonSize="sm"
                        buttonText="Print"
                        buttonOnClick={handlePrint}
                    />
                    <Btn
                        variant="secondary-outlined"
                        buttonSize="sm"
                        buttonText="Export PDF"
                        buttonOnClick={handlePDFExport}
                    />
                </div>
            </div>

            <div id="pm-report-detail-content" className="pm-service-queue-panel" style={{ padding: '24px' }}>
                <h2 style={{ margin: '0 0 20px', color: 'var(--navy-blue)', fontSize: '20px', fontWeight: 700 }}>
                    PM Report — {unitDisplayName || report.unitId}
                </h2>

                {/* Service Details */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '24px', padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
                    <div>
                        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>Service Level</div>
                        <div style={{ fontSize: '14px', color: 'var(--navy-blue)' }}>{report.serviceLevelName || report.serviceLevelId}</div>
                    </div>
                    <div>
                        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>Date Completed</div>
                        <div style={{ fontSize: '14px', color: 'var(--navy-blue)' }}>{formatDate(report.serviceDate)}</div>
                    </div>
                    <div>
                        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>Technician</div>
                        <div style={{ fontSize: '14px', color: 'var(--navy-blue)' }}>{report.technicianName || report.technicianId}</div>
                    </div>
                    <div>
                        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>Actual UM at Service</div>
                        <div style={{ fontSize: '14px', color: 'var(--navy-blue)' }}>{report.actualUM}</div>
                    </div>
                    <div>
                        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>Status</div>
                        <div style={{ fontSize: '14px', color: 'var(--navy-blue)' }}>{report.status || 'Completed'}</div>
                    </div>
                    <div>
                        <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 600, marginBottom: '4px' }}>Equipment</div>
                        <div style={{ fontSize: '14px', color: 'var(--navy-blue)' }}>{unitDisplayName || report.unitId}</div>
                    </div>
                </div>

                <PMIncompleteItemsSection
                    items={reportItems}
                    unitName={unitDisplayName || report.unitId}
                    serviceLevelName={report.serviceLevelName}
                    serviceDate={formatDate(report.serviceDate)}
                    pmConfigurationId={report.pmConfigurationId}
                />

                {/* Service Item Responses */}
                {reportItems.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--navy-blue)', marginBottom: '12px' }}>Service Items</h3>
                        <table className="pm-table">
                            <thead>
                                <tr>
                                    <th>Item</th>
                                    <th>Completed</th>
                                    <th>Qty</th>
                                    <th>Manufacturer</th>
                                    <th>Part #</th>
                                    <th>Vendor</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportItems.map((item, idx) => (
                                    <tr key={idx} style={{ cursor: 'default' }}>
                                        <td>{item.itemName || item.name || `Item ${idx + 1}`}</td>
                                        <td>{item.completed ? '✅' : '❌'}</td>
                                        <td>{item.quantity ?? '—'}</td>
                                        <td>{item.manufacturer || '—'}</td>
                                        <td>{item.partNumber || '—'}</td>
                                        <td>{item.vendorName || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Labor Cost Summary */}
                <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--navy-blue)', marginBottom: '12px' }}>Labor Cost Summary</h3>
                    <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px' }}>
                        <table className="pm-table">
                            <thead>
                                <tr>
                                    <th>Schedule</th>
                                    <th>ST Hours</th>
                                    <th>OT Hours</th>
                                    <th>ST Rate</th>
                                    <th>OT Rate</th>
                                    <th>Total Cost</th>
                                </tr>
                            </thead>
                            <tbody>
                                {laborLines.length > 0 ? (
                                    laborLines.map((line, index) => (
                                        <tr key={`${line.categoryId}-${index}`} style={{ cursor: 'default' }}>
                                            <td>{line.categoryName || '—'}</td>
                                            <td>{line.hoursST}</td>
                                            <td>{line.hoursOT}</td>
                                            <td>{formatCurrency(line.rateST)}</td>
                                            <td>{formatCurrency(line.rateOT)}</td>
                                            <td style={{ fontWeight: 600 }}>{formatCurrency(line.cost)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr style={{ cursor: 'default' }}>
                                        <td>{report.laborRateScheduleName || '—'}</td>
                                        <td>{report.laborHoursST ?? 0}</td>
                                        <td>{report.laborHoursOT ?? 0}</td>
                                        <td>{formatCurrency(report.laborRateST)}</td>
                                        <td>{formatCurrency(report.laborRateOT)}</td>
                                        <td style={{ fontWeight: 600 }}>{formatCurrency(computedLaborCost)}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Issues Found */}
                {report.issueDescription && (
                    <div style={{ marginBottom: '24px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--navy-blue)', marginBottom: '12px' }}>Issues Found</h3>
                        <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px', fontSize: '14px', color: 'var(--navy-blue)' }}>
                            <p style={{ margin: '0 0 8px' }}>{report.issueDescription}</p>
                            {report.issueTicketCreated && (
                                <span style={{ fontSize: '12px', color: '#3b82f6' }}>
                                    Issue Ticket Created{report.issueTicketId ? ` (ID: ${report.issueTicketId})` : ''}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* Photos */}
                {report.photos && report.photos.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--navy-blue)', marginBottom: '12px' }}>Attached Photos</h3>
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            {report.photos.map((photo, idx) => (
                                <div key={idx} style={{ width: '120px', height: '120px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                    <img src={photo} alt={`Photo ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Digital Signature */}
                {geotag && (
                    <div className="pm-report-geotag">
                        <h3>Location</h3>
                        <a
                            href={`https://maps.google.com/?q=${geotag.latitude},${geotag.longitude}`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {geotag.latitude.toFixed(5)}, {geotag.longitude.toFixed(5)}
                        </a>
                    </div>
                )}

                {report.signature && (
                    <div style={{ marginBottom: '24px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: 600, color: 'var(--navy-blue)', marginBottom: '12px' }}>Digital Signature</h3>
                        <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '16px', display: 'inline-block', border: '1px solid #e2e8f0' }}>
                            <img src={report.signature} alt="Signature" style={{ maxWidth: '300px', maxHeight: '100px' }} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
